import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { selectActiveOrg, useAuthStore } from '../auth/store';

// ---------------------------------------------------------------------------
// Types — mirror backend/src/modules/cashflow/cashflow.service.ts
// ---------------------------------------------------------------------------

export interface CashPeriodRow {
  key: string;
  year: number;
  month: number;
  half: 1 | 2;
  index: number;
  label: string;
  monthLabel: string;
  dayLabel: string;
  start: string;
  end: string;
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;

  startingCash: number;
  startingFromActual: boolean;
  mrrToBeBilled: number;
  income: number;
  collected: number;
  /**
   * Invoiced and not yet in the bank. **Sent invoices only** — a DRAFT has
   * never reached a client, so it is not owed, not expected here, and cannot be
   * rolled forward as overdue. Drafts have their own row in the Invoices block.
   */
  expected: number;
  rolledForward: number;
  rolledForwardCount: number;
  projectedAdditional: number;
  projectedPotential: number;
  expenses: number;
  endingCash: number;
  actualCash: number | null;
  /** `recorded` = typed into this cell; `snapshot` = derived from a dated cash
   *  snapshot inside the period (the same balances the dashboard reads). */
  actualSource: 'recorded' | 'snapshot' | null;
  /** The day the reading speaks for: a closed period's last day, or — for a
   *  fortnight still running — today. */
  actualAsOf: string | null;
  /** Where a `snapshot` actual came from: the bank's end-of-day balance, the
   *  QuickBooks balance sheet, or a snapshot somebody entered. */
  actualSnapshotSource: 'bank' | 'quickbooks' | 'manual' | null;
  actualNote: string | null;
  /**
   * ⚠ The period has NOT closed, so `actualCash` is a balance as of
   * `actualAsOf`, not a closing balance. The cell says so, and the chain does
   * not re-anchor on it (the fortnight still has money to collect and payroll
   * to pay). Same rule as a mid-period cash snapshot.
   */
  actualIsMidPeriod: boolean;
  /** actual − ending, on a CLOSED period only. Part of a fortnight minus a
   *  whole one is not a variance. */
  variance: number | null;

  /** The annual REVENUE goal ÷ 24 — a flat line. */
  goal: number;
  /**
   * The rising CASH-BALANCE target for this period (`cashGoalStart +
   * increment × index`). A bank balance, not revenue — the red line on the
   * cash chart. Never the same number as `goal`.
   */
  cashGoal: number;
  revenue: number;
  profit: number;
  profitYtd: number;
  /** Profit for the fiscal QUARTER so far — periods that have started only —
   *  on this grid's cash-FORECAST basis (collected + expected + recurring to
   *  be billed + projected, less cash out). */
  /** ⚠ NULL except in the quarter's LAST period — one figure per quarter,
   *  `close − open`, printed where the quarter ends. */
  profitQuarter: number | null;
  profitQuarterBasis: 'actual' | 'projected' | null;
  profitQuarterStart: number | null;
  /** The same quarter on CASH RECEIVED only. **The bonus is tested on this
   *  one**; on a closed quarter it equals `profitQuarter` by construction,
   *  because an elapsed period carries no expected or unbilled money. */
  profitQuarterReceived: number;
  /** `profitQuarterReceived` clears the plan's quarterly bonus profit minimum
   *  — received, not forecast, because a bonus is paid in cash. */
  bonusEarned: boolean;

  actualRecurring: number;
  actualSupport: number;
  actualAgreement: number;
}

export interface InvoiceBucket {
  count: number;
  total: number;
  due: number;
}

/** One half of the bonus test, with everything a failure needs to explain itself. */
export interface BonusCondition {
  met: boolean;
  actual: number;
  threshold: number;
  /** actual − threshold. Negative = short by this much. */
  gap: number;
}

/**
 * The bonus programme: cash on hand ≥ a floor AND profit for the fiscal
 * quarter above another. BOTH required, so both are reported separately —
 * "not met" that does not say which half failed is useless.
 */
export interface BonusStatus {
  met: boolean;
  cash: BonusCondition & {
    asOf: string | null;
    source: 'period-actual' | 'snapshot' | 'projected';
  };
  profit: BonusCondition & {
    /**
     * ⚠ **`actual` IS CASH RECEIVED LESS CASH OUT.** The cash report expects an
     * overdue invoice in the CURRENT period (the roll-forward rule), so a
     * quarter in progress can read almost entirely as money nobody has: Q3
     * 2026's forecast profit of $112,315 was $120,985 of overdue invoices (the
     * oldest 252 days) plus $3,025 not yet issued on top of −$11,695 of actual
     * cash. A bonus leaves the building in cash, so cash decides it — and these
     * four fields are the reconciliation the card prints in full:
     *
     *   actual (received) + expectedIncluded + unbilledIncluded = forecast
     */
    forecast: number;
    expectedIncluded: number;
    expectedInvoiceCount: number;
    /** Days past due of the oldest invoice rolled into the quarter. */
    expectedOldestDays: number;
    unbilledIncluded: number;
    /** The whole quarter on the FORECAST basis, including periods still to
     *  come. `actual` counts only periods that have started. */
    projected: number;
    quarter: number;
    label: string;
    start: string;
    end: string;
    complete: boolean;
    elapsedDays: number;
    totalDays: number;
    monthsCounted: number;
    monthsInQuarter: number;
  };
  fiscalYearStartMonth: number;
}

export interface CashflowReport {
  year: number;
  years: number[];
  today: string;
  currency: string;
  currentPeriodKey: string;
  periods: CashPeriodRow[];
  totals: {
    mrrToBeBilled: number;
    income: number;
    collected: number;
    expected: number;
    projectedAdditional: number;
    projectedPotential: number;
    expenses: number;
    revenue: number;
    profit: number;
    goal: number;
    goalPct: number | null;
    actualRecurring: number;
    actualSupport: number;
    actualAgreement: number;
    endingCash: number;
  };
  /**
   * **ARE WE ON PACE? — the honest reading of "Revenue vs goal".**
   *
   * `totals.revenue` / `totals.goalPct` are a full-year PROJECTION against a
   * full-year goal (98.7% on 2026-08-12) while the year to date was 85.6% of
   * pace and $164,465 behind. Both sides of this block cover the SAME elapsed
   * calendar: `booked` is revenue in the half-month periods that have closed,
   * `goalToDate` is the goal line summed over exactly those periods — the same
   * cut the revenue chart's solid vs hatched bars make.
   */
  revenuePace: {
    periodsElapsed: number;
    periodsInYear: number;
    through: string | null;
    throughLabel: string | null;
    booked: number;
    goalToDate: number;
    /** booked ÷ goalToDate — **attainment**. null when nothing has closed. */
    pct: number | null;
    /** booked − goalToDate. Negative = behind pace. */
    gap: number;
    /** Where the pace marker sits on a full-year bar. */
    elapsedPct: number | null;
    bookedPct: number | null;
    projected: number;
    projectedPct: number | null;
    /** Projected lines + ticked deals: revenue nobody has invoiced yet. */
    unbooked: number;
  };
  invoices: {
    draft: InvoiceBucket;
    sent: InvoiceBucket;
    overdue: InvoiceBucket;
    paid: InvoiceBucket;
  };
  breakdown: {
    monthsElapsed: number;
    /** support + recurring. "Program" is not a word this business uses. */
    supportRecurringTotal: number;
    supportTotal: number;
    recurringTotal: number;
    agreementTotal: number;
    avgSupportMonthly: number;
    avgRecurringMonthly: number;
    avgAgreementMonthly: number;
  };
  runway: {
    currentCash: number;
    asOf: string | null;
    source: 'period-actual' | 'snapshot' | 'projected';
    /** Liabilities on the latest snapshot at/before today, with its own date —
     *  cash on hand alone overstates what can actually be spent. */
    liabilities: number | null;
    liabilitiesAsOf: string | null;
    /** currentCash − liabilities; null when no snapshot records them. */
    netCash: number | null;
    /** GROSS cash out per month — money leaving, income ignored. */
    monthlyBurn: number;
    /**
     * ⚠ The numerator BOTH durations below divide: net cash when a snapshot
     * records liabilities, cash on hand when none does. `basis` names it, and
     * the screen prints that name — a duration struck on gross cash beside a
     * tile reading "less $49,700 liabilities = $193,660 net" is a contradiction.
     */
    runwayCash: number;
    basis: 'net-cash' | 'cash';
    /** Runway cash ÷ gross outflow: the ZERO-REVENUE stress test. Not runway. */
    months: number | null;
    netMonthly: number;
    netPositive: boolean;
    /** **Runway**: cash ÷ net burn. `null` while cash-flow positive — a growing
     *  balance has no runway, and printing a number there contradicts
     *  `netMonthly` on the same card. */
    netMonths: number | null;
    /** Net burn as a positive number; null while cash-flow positive. */
    netBurn: number | null;
    /** ⚠ `break-even` = |net| is inside 10% of gross outflow, i.e. noise. No
     *  headline may print a duration (or ∞) there — see the server's note.
     *  Optional so a screen served by an API that predates the band falls back
     *  to the old sign test rather than rendering an empty tile. */
    netBand?: 'burning' | 'break-even' | 'positive';
    /** The band's threshold in dollars/month, so the screen can state it. */
    netBandLimit?: number;
    windowMonths: number;
    /** The TRAILING months averaged for `monthlyBurn` — e.g. "May–Jul". */
    windowLabel: string;
    /**
     * The PLANNED rate, from the months ahead — what the business is budgeting
     * to, as opposed to what it has been spending. After a deliberate cost
     * change these answer different questions, so both are shown and each names
     * its window. `null` when no full month is left in the year to plan against.
     */
    plannedMonthlyOut: number | null;
    plannedWindowLabel: string | null;
    plannedMonths: number | null;
    /** Has the cost base actually moved (>10%)? Only then are two figures worth printing. */
    planDiffers: boolean;
    /**
     * The first half-month from today whose PROJECTED balance is below $0,
     * looking `runOutHorizonMonths` ahead across years; null when it never is.
     */
    runOut?: {
      key: string;
      label: string;
      year: number;
      start: string;
      end: string;
      months: number;
      endingCash: number;
    } | null;
    runOutThrough?: string;
    runOutHorizonMonths?: number;
  };
  plan: {
    year: number;
    /** Annual REVENUE goal — "Revenue vs goal". */
    goal: number;
    /** Quarterly profit floor for the bonus programme. */
    bonusProfitMin: number;
    /** Cash-on-hand floor for the bonus programme. */
    bonusCashMin: number;
    /** Start and per-period rise of the CASH-BALANCE goal line. */
    cashGoalStart: number;
    cashGoalIncrement: number;
    /** `default` = derived from the year's opening cash + $10,000/period
     *  because nobody has set it; `plan` = stored on the cash plan. */
    cashGoalSource: 'plan' | 'default';
    /** The cash the year opens on — every column is a running total from it. */
    openingCash: number;
    /** `plan` = a human typed it. Everything else is the app reconstructing it. */
    openingCashSource:
      | 'plan'
      | 'prior-year'
      | 'prior-close'
      | 'snapshot-before'
      | 'first-close'
      | 'snapshot-in-year'
      | 'none';
    saved: boolean;
  };
  bonus: BonusStatus;
  recurring: {
    activeCount: number;
    /** AVERAGE worth per month (quarterly ÷ 3, annual ÷ 12) — never what a
     *  period column shows: a $12k annual bills $12k in ONE period. */
    monthlyValue: number;
    /** What the active schedules actually bill over the next 12 months. */
    next12Months: number;
    /** Schedules that are not monthly — the ones "MRR" mis-describes. */
    nonMonthlyCount: number;
    next: Array<{
      id: string;
      client: string;
      subject: string;
      amount: number;
      nextIssueDate: string;
      cadence: string;
      cadenceLabel: string;
    }>;
  };
  meta: {
    expensesSource: 'schedule' | 'booked' | 'none';
    recurringSource: 'templates' | 'forecast';
    scheduleRowCount: number;
    notes: string[];
  };
}

// ---------------------------------------------------------------------------
// Hooks — keys start with ['cashflow', orgId] so an org switch refetches.
// ---------------------------------------------------------------------------

export function useActiveOrgId(): string | undefined {
  return useAuthStore((s) => selectActiveOrg(s)?.id);
}

export function useCashflow(year: number) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['cashflow', orgId, year],
    queryFn: () => api.get<CashflowReport>('/cashflow', { params: { year } }),
    enabled: Boolean(orgId),
    // Stepping the year is a re-read of the same report, not a new page: keep
    // the grid on screen rather than flashing 24 columns of skeleton.
    placeholderData: (previous) => previous,
  });
}

export interface ActualInput {
  year: number;
  month: number;
  half: 1 | 2;
  /** null clears the reading — a mistyped balance must be removable from the
   *  same cell that created it. */
  actualCash: number | null;
}

/**
 * Record one period's reconciled cash balance. Optimistic: the grid re-chains
 * starting/ending cash locally the moment the cell commits, because the whole
 * point of typing it there is watching the projection correct itself.
 */
export function useSetActual(year: number) {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  const key = ['cashflow', orgId, year];
  return useMutation({
    mutationFn: (input: ActualInput) => api.put<unknown>('/cashflow/actual', input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CashflowReport>(key);
      if (previous) queryClient.setQueryData(key, applyActual(previous, input));
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['cashflow', orgId] }),
  });
}

/**
 * Every field is optional and only what is sent gets written, so one inline
 * edit on the plan bar never clobbers its neighbours.
 *
 * ⚠ `goal` is the annual REVENUE goal; `cashGoalStart`/`cashGoalIncrement`
 * describe the rising CASH-BALANCE line on the chart. Two targets.
 */
export interface PlanPatch {
  goal?: number;
  bonusProfitMin?: number;
  bonusCashMin?: number;
  cashGoalStart?: number;
  cashGoalIncrement?: number;
  /** The year's opening balance. `null` clears it and reverts to derivation. */
  openingCash?: number | null;
}

export function useSetPlan(year: number) {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  return useMutation({
    mutationFn: (input: PlanPatch) => api.put<unknown>('/cashflow/plan', { year, ...input }),
    // The dashboard reads the same report — a threshold changed on the Report
    // tab must move the bonus card, not wait for a reload.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cashflow', orgId] }),
  });
}

// ---------------------------------------------------------------------------
// Local re-chaining — the same arithmetic the server does, in integer cents.
// ---------------------------------------------------------------------------

const cents = (value: number) => Math.round(value * 100);
const dollars = (value: number) => Math.round(value) / 100;

/**
 * Re-walk the 24 columns after one actual changes.
 *
 * Starting cash re-anchors on a recorded balance from a **CLOSED** period and
 * otherwise carries the previous projection, exactly as the server does — if
 * these two ever disagree the grid would visibly jump when the refetch lands.
 * A reading typed into the fortnight still running is kept, marked
 * `actualIsMidPeriod`, and left out of the chain.
 */
export function applyActual(report: CashflowReport, input: ActualInput): CashflowReport {
  const targetKey = `${input.year}-${String(input.month).padStart(2, '0')}-H${input.half}`;
  let carry = cents(report.periods[0]?.startingCash ?? 0);
  let carryFromActual = report.periods[0]?.startingFromActual ?? false;
  let profitYtd = 0;

  const periods = report.periods.map((period) => {
    const actual =
      period.key === targetKey
        ? input.actualCash === null
          ? null
          : cents(input.actualCash)
        : period.actualCash === null
          ? null
          : cents(period.actualCash);
    const ending =
      carry +
      cents(period.mrrToBeBilled) +
      cents(period.income) +
      cents(period.projectedAdditional) -
      cents(period.expenses);
    const revenue = cents(period.revenue);
    profitYtd += revenue - cents(period.expenses);

    // `isPast` is the server's own "this fortnight has closed" (end < today),
    // so the two sides of the optimistic swap cannot disagree about which
    // readings are closing balances.
    const midPeriod = actual !== null && !period.isPast;
    const anchors = actual !== null && period.isPast;

    const next: CashPeriodRow = {
      ...period,
      startingCash: dollars(carry),
      startingFromActual: carryFromActual,
      endingCash: dollars(ending),
      actualCash: actual === null ? null : dollars(actual),
      actualSource:
        period.key === targetKey
          ? input.actualCash === null
            ? null
            : 'recorded'
          : period.actualSource,
      actualAsOf:
        period.key === targetKey
          ? input.actualCash === null
            ? null
            : midPeriod
              ? report.today
              : period.end
          : period.actualAsOf,
      actualSnapshotSource: period.key === targetKey ? null : period.actualSnapshotSource,
      actualNote: period.key === targetKey && input.actualCash === null ? null : period.actualNote,
      actualIsMidPeriod: midPeriod,
      variance: anchors ? dollars(actual! - ending) : null,
      profitYtd: dollars(profitYtd),
    };
    carryFromActual = anchors;
    carry = anchors ? actual! : ending;
    return next;
  });

  return {
    ...report,
    periods,
    totals: { ...report.totals, endingCash: periods[periods.length - 1]?.endingCash ?? 0 },
  };
}
